/**
 * MCP - Model Context Protocol - connector management.
 *
 * The org admin enables platform-curated public MCP servers; users see
 * what's enabled and (V2) request per-user grants for BYO servers.
 *
 * Backend routes live under /api/ddr/mcp (see ddr_router.py). This
 * service wraps them with explicit types so callers don't have to deal
 * with raw HTTP shapes.
 */

import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';

import { environment } from '../../environments/environment';

const ROOT = `${environment.apiBase}/api/ddr/mcp`;

export type MCPAuthType = 'none' | 'api_key' | 'oauth2';

/** Platform-curated public catalog entry. Admin opts the org in via
 *  enablePublic; private CRUD is on `MCPServer` rows. */
export type PublicMCPEntry = {
    id?: string;
    name: string;
    description: string;
    server_url: string;
    auth_type: MCPAuthType;
    requires_byo_creds: boolean;
    enabled?: boolean;
    enabled_workspace_count?: number;
};

/** A server row attached to a company - either via public-catalog enable
 *  or custom admin creation. `auth_config` is REDACTED on the wire. */
export type MCPServer = {
    id: string;
    company_id: number | null;
    workspace_id: number;
    user_id: number | null;
    catalog_entry_id?: string;
    name: string;
    description?: string | null;
    server_url: string;
    auth_type: MCPAuthType;
    is_public_curated: boolean;
    is_catalog?: boolean;
    enabled: boolean;
    health_status: 'unknown' | 'ok' | 'down' | 'auth_failed' | string;
    admin_blocked_at?: string | null;
    admin_block_reason?: string | null;
    last_health_check_at: string | null;
    created_at: string;
};

export type MCPScope = 'personal' | 'workspace';

/** One tool as the server describes itself. */
export type MCPProbeTool = {
    name: string;
    description: string;
    input_schema: Record<string, unknown>;
};

/** Result of a read-only probe. `stage` says how far the exchange got, which
 *  is the most useful field when diagnosing — "handshake fine, tools/list
 *  failed" and "never connected" have completely different fixes. Fields after
 *  `duration_ms` are absent on early-stage failures. */
export type MCPProbeResult = {
    reachable: boolean;
    stage: 'lookup' | 'auth' | 'handshake' | 'tools' | 'complete';
    error: string | null;
    duration_ms: number;
    /** Set when the failure is the caller's missing connection, not an outage. */
    needs_user_connect?: boolean;
    handshake_ms?: number;
    protocol_version?: string | null;
    server_name?: string | null;
    server_version?: string | null;
    capabilities?: Record<string, unknown>;
    tool_count?: number;
    tools?: MCPProbeTool[];
    /** HTTP status when the server answered. Null for transport failures, which
     *  are the only case that genuinely means "unreachable". */
    http_status?: number | null;
    /** The provider's 401/403 challenge. Names the expected token audience on a
     *  spec-compliant MCP server, so it is the field that explains a rejected
     *  bearer that no other field can. */
    www_authenticate?: string | null;
    /** True when the server refused our credential rather than failing to
     *  answer. Drives the copy: reconnect, not wait. */
    auth_failed?: boolean;
    auth_type?: string;
    /** Non-secret token facts (fingerprints + allowlisted JWT claims). Never
     *  carries the token itself. */
    token?: Record<string, unknown> | null;
};

export type MCPMemberPermission = {
    user_id: number;
    email: string;
    first_name: string | null;
    last_name: string | null;
    role: 'owner' | 'admin' | 'member' | string;
    can_manage_mcps: boolean;
};

/** Non-secret token hints the list endpoints return. The raw tokens never
 *  leave the API; `needs_reconnect` is stamped server-side when a refresh
 *  fails so the client can toast without waiting for another 401. */
export type MCPGrantAuthHints = {
    has_access_token?: boolean;
    has_refresh_token?: boolean;
    scope?: string | null;
    expires_at?: string | null;
    needs_reconnect?: boolean;
    /** Our lifecycle's marker: 'needs_reconnect' = the tokens died (permanent
     *  refresh failure / provider 401). Same meaning as `needs_reconnect`;
     *  both are honored by connectionHealth.mcpGrantIsBroken. */
    auth_state?: 'needs_reconnect' | null;
};

export type MCPGrant = {
    id: string;
    user_id: number;
    org_mcp_server_id: string;
    catalog_entry_id?: string;
    revoked_at: string | null;
    created_at: string;
    /** Redacted token summary from the backend (never the tokens themselves).
     *  auth_state 'needs_reconnect' = the tokens died (permanent refresh
     *  failure or a provider 401) - render Reconnect, not Connected. */
    user_auth_config?: MCPGrantAuthHints | null;
};

@Injectable({ providedIn: 'root' })
export class McpService {
    private readonly http = inject(HttpClient);

    listPublicCatalog(): Promise<PublicMCPEntry[]> {
        return firstValueFrom(this.http.get<PublicMCPEntry[]>(`${ROOT}/public_catalog`));
    }

    listMyServers(personaId?: string): Promise<MCPServer[]> {
        const url = personaId ? `${ROOT}/my_servers?persona_id=${encodeURIComponent(personaId)}` : `${ROOT}/my_servers`;
        return firstValueFrom(this.http.get<MCPServer[]>(url));
    }

    listOrgServers(enabledOnly = false): Promise<MCPServer[]> {
        const qs = enabledOnly ? '?enabled_only=true' : '';
        return firstValueFrom(this.http.get<MCPServer[]>(`${ROOT}/servers${qs}`));
    }

    listMyGrants(): Promise<MCPGrant[]> {
        return firstValueFrom(this.http.get<MCPGrant[]>(`${ROOT}/my_grants`));
    }

    /** Admin: stand up an org-scoped row from the public catalog. */
    enablePublic(publicName: string, authConfigOverrides?: Record<string, unknown>): Promise<MCPServer> {
        return firstValueFrom(
            this.http.post<MCPServer>(`${ROOT}/servers/enable_public`, {
                public_name: publicName,
                auth_config_overrides: authConfigOverrides ?? {},
                enabled: true,
            })
        );
    }

    /** Admin: attach a custom (non-catalog) MCP server. `auth_config` shape
     *  depends on auth_type - `{api_key, header_name, scheme}` for api_key,
     *  `{client_id, client_secret, token_url, scope}` for oauth2, `{}` for none. */
    createServer(body: {
        name: string;
        server_url: string;
        auth_type: MCPAuthType;
        auth_config: Record<string, unknown>;
        scope: MCPScope;
        enabled?: boolean;
    }): Promise<MCPServer> {
        return firstValueFrom(this.http.post<MCPServer>(`${ROOT}/servers`, { enabled: true, ...body }));
    }

    promoteServer(serverId: string): Promise<MCPServer> {
        return firstValueFrom(this.http.post<MCPServer>(`${ROOT}/servers/${serverId}/promote`, {}));
    }

    /** Admin: remove an org-scoped server (irreversible). */
    deleteServer(serverId: string): Promise<void> {
        return firstValueFrom(this.http.delete<void>(`${ROOT}/servers/${serverId}`));
    }

    /** Admin: toggle enabled + auth_config updates on an org-scoped row. */
    patchServer(serverId: string, body: { enabled?: boolean; auth_config?: Record<string, unknown> }): Promise<MCPServer> {
        return firstValueFrom(this.http.patch<MCPServer>(`${ROOT}/servers/${serverId}`, body));
    }

    healthCheck(serverId: string): Promise<{ health_status: string; checked_at: string }> {
        return firstValueFrom(
            this.http.post<{ health_status: string; checked_at: string }>(`${ROOT}/servers/${serverId}/health_check`, {})
        );
    }

    /** Read-only diagnostic against one server, using the caller's own
     *  credentials. Persists nothing — unlike `healthCheck`, which writes the
     *  shared `health_status` and stays admin-only (ADR-0022). Surfaced under
     *  MCP rows in Developer Mode. */
    probe(serverId: string): Promise<MCPProbeResult> {
        return firstValueFrom(this.http.post<MCPProbeResult>(`${ROOT}/servers/${serverId}/probe`, {}));
    }

    /** Begin the per-user OAuth flow against an oauth2 MCP server.
     *  Returns the provider's authorize URL - caller window.opens it
     *  (or full-page redirects) so the user can consent. */
    oauthStart(serverId: string): Promise<{ authorize_url: string; server_id: string; redirect_uri: string; state: string }> {
        return firstValueFrom(
            this.http.post<{ authorize_url: string; server_id: string; redirect_uri: string; state: string }>(
                `${ROOT}/grants/${serverId}/oauth/start`,
                {}
            )
        );
    }

    /** Per-user grant creation (for non-public servers). */
    createGrant(serverId: string): Promise<MCPGrant> {
        return firstValueFrom(this.http.post<MCPGrant>(`${ROOT}/grants`, { org_mcp_server_id: serverId }));
    }

    deleteGrant(grantId: string): Promise<void> {
        return firstValueFrom(this.http.delete<void>(`${ROOT}/grants/${grantId}`));
    }

    /** Org admin: active workspace members with their Manage-MCPs flag. */
    listManagePermissions(): Promise<MCPMemberPermission[]> {
        return firstValueFrom(this.http.get<MCPMemberPermission[]>(`${ROOT}/members`));
    }

    setManagePermission(userId: number, canManageMcps: boolean): Promise<{ user_id: number; can_manage_mcps: boolean }> {
        return firstValueFrom(
            this.http.put<{ user_id: number; can_manage_mcps: boolean }>(`${ROOT}/members/${userId}/manage-permission`, {
                can_manage_mcps: canManageMcps,
            })
        );
    }

    listAdminCatalog(): Promise<PublicMCPEntry[]> {
        return firstValueFrom(this.http.get<PublicMCPEntry[]>(`${environment.apiBase}/api/ddr/admin/mcp/catalog`));
    }

    createAdminCatalogEntry(body: {
        name: string;
        description: string;
        server_url: string;
        auth_type: MCPAuthType;
        auth_config_template: Record<string, unknown>;
        enabled?: boolean;
    }): Promise<PublicMCPEntry> {
        return firstValueFrom(this.http.post<PublicMCPEntry>(`${environment.apiBase}/api/ddr/admin/mcp/catalog`, body));
    }

    patchAdminCatalogEntry(
        catalogEntryId: string,
        body: Partial<{
            name: string;
            description: string;
            server_url: string;
            auth_type: MCPAuthType;
            auth_config_template: Record<string, unknown>;
            enabled: boolean;
        }>
    ): Promise<PublicMCPEntry> {
        return firstValueFrom(this.http.patch<PublicMCPEntry>(`${environment.apiBase}/api/ddr/admin/mcp/catalog/${catalogEntryId}`, body));
    }

    listAdminServers(): Promise<MCPServer[]> {
        return firstValueFrom(this.http.get<MCPServer[]>(`${environment.apiBase}/api/ddr/admin/mcp/servers`));
    }

    blockAdminServer(serverId: string, reason: string): Promise<{ server_id: string; admin_blocked: boolean; admin_block_reason: string }> {
        return firstValueFrom(
            this.http.post<{ server_id: string; admin_blocked: boolean; admin_block_reason: string }>(
                `${environment.apiBase}/api/ddr/admin/mcp/servers/${serverId}/block`,
                { reason }
            )
        );
    }

    unblockAdminServer(serverId: string): Promise<{ server_id: string; admin_blocked: boolean }> {
        return firstValueFrom(
            this.http.post<{ server_id: string; admin_blocked: boolean }>(
                `${environment.apiBase}/api/ddr/admin/mcp/servers/${serverId}/unblock`,
                {}
            )
        );
    }
}
